import { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import RightDialer from "./RightDialer";
import IncomingCallToast from "./IncomingCallToast";
import NotificationBell from "./NotificationBell";
import CoachReactionOverlay from "./CoachReactionOverlay";
import FloatingTools from "./productivity/FloatingTools";
import BrandWatermark from "./BrandWatermark";
import ActiveCallBanner from "./ActiveCallBanner";
import { ImpersonationBanner } from "@/lib/impersonation";
import { Outlet, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function AppLayout() {
  const { user, loading } = useAuth();
  // Phase 3 force-redirect: if the user's org has any Communication Seat
  // still in `lifecycle_state="purchased"` (i.e., a number has NOT been
  // bound yet), they MUST complete the Welcome Wizard first. Once numbers
  // are bound (`number_assigned`), the admin can proceed to the dashboard
  // and assign seats to staff on their own time from the Numbers page.
  const [wizardCheck, setWizardCheck] = useState("checking");  // checking | required | ok
  useEffect(() => {
    if (!user || user.role !== "admin") { setWizardCheck("ok"); return; }
    (async () => {
      try {
        const { data } = await api.get("/onboarding/seats");
        const needs = (data.seats || []).some(
          (s) => s.lifecycle_state === "purchased",
        );
        setWizardCheck(needs ? "required" : "ok");
      } catch {
        setWizardCheck("ok"); // fail-open; existing users pre-Phase-3 have no seats endpoint issues
      }
    })();
  }, [user]);

  // Hydration gate: hold rendering the whole app shell (Sidebar / Outlet /
  // Dialer / Notifications) until the AuthContext knows who the user is.
  // Without this, an agent lands on /app during the ~150-400ms while
  // /auth/me is in flight, child pages evaluate `user?.role`, see
  // `undefined`, and render their "not entitled / locked" state. When
  // /auth/me resolves seconds later, many of those child components DON'T
  // re-run their permission check — the customer sees a locked UI until
  // they manually refresh. Rendering a lightweight placeholder while
  // loading closes that race entirely.
  if (loading) {
    return (
      <div
        className="h-screen flex items-center justify-center bg-[var(--bg)]"
        data-testid="app-layout-hydrating"
      >
        <div className="flex flex-col items-center gap-3">
          <div className="w-6 h-6 rounded-full border-2 border-[var(--accent)] border-t-transparent animate-spin" aria-hidden />
          <div className="font-mono text-[10.5px] tracking-widest text-[var(--text-muted)]">LOADING WORKSPACE…</div>
        </div>
      </div>
    );
  }

  if (wizardCheck === "required") return <Navigate to="/welcome" replace />;

  return (
    <div className="h-screen flex flex-col hbf-app-canvas bg-[var(--bg)] text-[var(--text-primary)]" data-testid="app-layout">
      <BrandWatermark />
      <ImpersonationBanner />
      <ActiveCallBanner />
      <div className="flex flex-1 min-h-0 relative z-[1]">
        <Sidebar />
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
        <RightDialer />
        <IncomingCallToast />
        <NotificationBell />
        <CoachReactionOverlay />
        <FloatingTools />
      </div>
    </div>
  );
}
